import { useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function validPhone(v) {
  if (!/^\+[\d\s().-]+$/.test(v)) return false;
  const digits = v.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
}

export default function WaitlistDialog({ open, onClose, onJoined }) {
  const ref = useRef(null);
  const emailRef = useRef(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [status, setStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      emailRef.current?.focus();
    } else if (!open && d.open) {
      d.close();
    }
  }, [open]);

  const handleNativeClose = () => onClose();

  const submit = async (e) => {
    e.preventDefault();
    setStatus("");
    const e1 = EMAIL.test(email.trim()) ? "" : "Enter a valid email address.";
    const p = phone.trim();
    const e2 = !p || validPhone(p) ? "" : "Enter a valid phone number, with country code.";
    setEmailErr(e1);
    setPhoneErr(e2);
    if (e1 || e2) return;

    setSending(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        phone: p,
        source: "waitlist",
      });
      setDone(true);
      onJoined();
    } catch {
      setStatus("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <dialog
      ref={ref}
      aria-labelledby="wl-title"
      aria-describedby="wl-lead"
      data-testid="waitlist-dialog"
      onClose={handleNativeClose}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={`sheet${done ? " is-done" : ""}`}>
        <button
          className="close"
          type="button"
          aria-label="Close"
          data-testid="waitlist-close-button"
          onClick={onClose}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        <div className="form">
          <h2 id="wl-title">Join the waitlist</h2>
          <p className="lead" id="wl-lead">Be the first in when Frank369 AI opens.</p>

          <form onSubmit={submit} noValidate>
            <div className="fields">
              <div className="field">
                <label htmlFor="wl-email">Email</label>
                <input
                  id="wl-email"
                  ref={emailRef}
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  required
                  aria-invalid={emailErr ? "true" : "false"}
                  data-testid="waitlist-email-input"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailErr) setEmailErr("");
                  }}
                />
                <p className="err">{emailErr}</p>
              </div>
              <div className="field">
                <label htmlFor="wl-phone">
                  Phone number <span className="opt">(optional)</span>
                </label>
                <input
                  id="wl-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="+1 555 123 4567"
                  aria-invalid={phoneErr ? "true" : "false"}
                  data-testid="waitlist-phone-input"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (phoneErr) setPhoneErr("");
                  }}
                />
                <p className="err">{phoneErr}</p>
              </div>
            </div>
            <button
              className="btn submit"
              type="submit"
              disabled={sending}
              data-testid="waitlist-submit-button"
            >
              {sending ? "Joining…" : "Join the waitlist"}
            </button>
            <p className="status" role="alert" data-testid="waitlist-status">{status}</p>
            <p className="fine">
              By joining, you agree to receive email and SMS marketing about
              Frank369 AI early access. Unsubscribe anytime.
            </p>
          </form>
        </div>

        <div className="done" tabIndex={-1} data-testid="waitlist-done-card">
          <div className="tick" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h2>You're on the list.</h2>
          <p className="lead">We'll reach out when Frank369 AI opens.</p>
          <button className="btn" type="button" onClick={onClose} data-testid="waitlist-done-button">
            Done
          </button>
        </div>
      </div>
    </dialog>
  );
}
